/* eslint-disable eqeqeq */
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import {
    DataTable,
    DataTableExpandedRows,
    DataTableValueArray,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Sidebar } from "primereact/sidebar";
import { Toast } from "primereact/toast";
import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    CategoryDetail,
    Category as Ctg,
    DropdownInterface,
} from "../../../constants/interface";
import { useSpinner } from "../../../custom-hook/SpinnerContext";
import { storage } from "../../../firebase/firebaseConfig";
import ApiService from "../../../services/api.service";
import ImagePickerDialog from "../../PickerDialog/ImagePickerDialog";
import "./Category.scss";

const HOME_BREADCRUMB = { icon: "pi pi-home", url: "" };
const BREADCRUMB_ITEMS = [{ label: "Phân loại" }, { label: "Loại" }];
const EMPTY_IMAGE_URL = "https://hochieuqua7.web.app/images/admin/setting/slide/empty-image.png";

interface CombinedCategory extends Ctg {
    childs: CategoryDetail[];
}

function Category() {
    const toast = useRef<Toast>(null);
    const op = useRef<OverlayPanel>(null);
    const op2 = useRef<OverlayPanel>(null);

    const [selectedId, setSelectedId] = useState<string>();
    const [selectedDetailId, setSelectedDetailId] = useState<string>();
    const [detailCtg, setDetailCtg] = useState<Ctg>(new Ctg());
    const [detailDetailCtg, setDetailDetailCtg] = useState<CategoryDetail>(
        new CategoryDetail()
    );
    const [combinedData, setCombinedData] = useState<CombinedCategory[]>([]);
    const [visibleRight, setVisibleRight] = useState(false);
    const [visibleLeft, setVisibleLeft] = useState(false);
    const [listCtg, setListCtg] = useState<DropdownInterface[]>([]);
    const [image, setImage] = useState<File | null>(null);
    const [isChangeAvatar, setIsChangeAvatar] = useState<boolean>(false);
    const [imageUrl, setImageUrl] = useState<string>("");
    const [showImagePicker, setShowImagePicker] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<
        DataTableExpandedRows | DataTableValueArray | undefined
    >(undefined);
    const { showSpinner, hideSpinner } = useSpinner();

    const fetchCategoriesAndDetails = useCallback(async () => {
        try {
            const [categoryResponse, categoryDetailResponse] =
                await Promise.all([
                    ApiService.getCategoryList(
                        queryString.stringify({ filter: "" })
                    ), // Giả sử đây là API lấy danh sách category
                    ApiService.getCategoryDetailList(
                        queryString.stringify({
                            category_code: "",
                            screen: "admin",
                        })
                    ), // Giả sử đây là API lấy danh sách category detail
                ]);

            const categories: Ctg[] = categoryResponse?.data?.data ?? [];
            setListCtg(categories.map((item) => ({
                label: item.name,
                value: item.id || "",
            })));
            const categoryDetails: CategoryDetail[] = categoryDetailResponse?.data?.data ?? [];

            return { categories, categoryDetails };
        } catch (err) {
            console.error(err);
            return { categories: [], categoryDetails: [] };
        }
    }, []);

    const fetchData = useCallback(async () => {
        const { categories, categoryDetails } =
            await fetchCategoriesAndDetails();
        const combined: CombinedCategory[] = categories.map((category) => ({
            ...category,
            childs: categoryDetails.filter(
                (detail) => detail.category_id === category.id
            ),
        }));
        setCombinedData(combined);
    }, [fetchCategoriesAndDetails]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const allowExpansion = useCallback((rowData: CombinedCategory) => {
        return rowData.childs?.length > 0;
    }, []);

    const imageBodyTemplate = useCallback((category: CombinedCategory) => (
        <img
            src={category.img}
            alt={category.img}
            style={{ objectFit: "contain" }}
            className="w-9rem h-3rem shadow-2 border-round"
        />
    ), []);

    const statusTemplate = useCallback((category: CombinedCategory) => (
        <Checkbox checked={String(category.status) === "1"}></Checkbox>
    ), []);

    const showHomeTemplate = useCallback((category: CombinedCategory) => (
        <Checkbox checked={String(category.is_show_home) === "1"}></Checkbox>
    ), []);

    const optionsTemplate = useCallback((rowData: CombinedCategory) => (
        <span
            className="flex justify-content-center"
            onClick={(e) => {
                op.current?.toggle(e);
                setSelectedId(rowData.id);
            }}
        >
            <i className="pi pi-ellipsis-v"></i>
        </span>
    ), []);

    const optionsTemplate2 = useCallback((rowData: CategoryDetail) => (
        <span
            className="flex justify-content-center"
            onClick={(e) => {
                op2.current?.toggle(e);
                setSelectedDetailId(rowData.id);
            }}
        >
            <i className="pi pi-ellipsis-v"></i>
        </span>
    ), []);

    const acceptCtg = useCallback(async () => {
        try {
            const deleteCtg = await ApiService.deleteCategory(selectedId || "");
            if (deleteCtg.status === "success") {
                toast.current?.show({
                    severity: "success",
                    summary: "Thông báo",
                    detail: "Xóa bản ghi thành công !",
                    life: 3000,
                });
                fetchData();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Thông báo",
                    detail: deleteCtg.message || "Không thành công !",
                    life: 3000,
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Thông báo",
                detail: "Không thành công !",
                life: 2000,
            });
        }
    }, [selectedId, fetchData]);

    const acceptCtgDetail = useCallback(async () => {
        try {
            const deleteCtg = await ApiService.deleteCategoryDetail(
                selectedDetailId || ""
            );
            toast.current?.show({
                severity:
                    deleteCtg.status === "success" ? "success" : "error",
                summary: "Thông báo",
                detail: deleteCtg.message,
                life: 3000,
            });
            if (deleteCtg.status === "success") {
                fetchData();
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "Thông báo",
                detail: "Không thành công !",
                life: 2000,
            });
        }
    }, [selectedDetailId, fetchData]);

    const confirmDelete = useCallback(() => {
        confirmDialog({
            header: "Xác nhận",
            message: "Bạn muốn xóa bản ghi này không ?",
            icon: "pi pi-info-circle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Xóa",
            rejectLabel: "Hủy",
            accept: acceptCtg,
        });
    }, [acceptCtg]);

    const confirmDeleteCtgDetail = useCallback(() => {
        confirmDialog({
            header: "Xác nhận",
            message: "Bạn muốn xóa bản ghi này không ?",
            icon: "pi pi-info-circle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Xóa",
            rejectLabel: "Hủy",
            accept: acceptCtgDetail,
        });
    }, [acceptCtgDetail]);

    const uploadAvatar = useCallback((): Promise<void> => {
        showSpinner();
        return new Promise((resolve, reject) => {
            if (image) {
                const storageRef = ref(storage, `images/${image.name}`);
                const uploadTask = uploadBytesResumable(storageRef, image);

                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        // Tiến trình tải lên
                    },
                    (error) => {
                        console.error("Upload failed", error);
                        reject(error);
                    },
                    () => {
                        getDownloadURL(uploadTask.snapshot.ref).then(
                            (downloadURL1) => {
                                setImageUrl(downloadURL1);
                                hideSpinner();
                                resolve();
                            }
                        );
                    }
                );
            } else {
                resolve();
            }
        });
    }, [image, showSpinner, hideSpinner]);

    const fetchCtgDetail = useCallback(async () => {
        try {
            const detail = await ApiService.getCategoryDetail(selectedId || "");
            setDetailCtg(detail.data);
            setImageUrl(detail.data.img);
        } catch (error) {
            console.log(error);
        }
    }, [selectedId]);

    const fetchCtgDetailDetail = useCallback(async () => {
        try {
            const detail = await ApiService.getCategoryDetailDetail(
                selectedDetailId || ""
            );
            setDetailDetailCtg(() => ({
                ...detail.data,
                size: detail.data.size ? detail.data.size.split(',') : [],
            }));
        } catch (error) {
            console.log(error);
        }
    }, [selectedDetailId]);

    const viewCtgDetail = useCallback(() => {
        fetchCtgDetail();
        setVisibleRight(true);
    }, [fetchCtgDetail]);

    const handleEditDetailDetailCtg = useCallback(() => {
        fetchCtgDetailDetail();
        setVisibleLeft(true);
    }, [fetchCtgDetailDetail]);

    const handleCtgChange = useCallback((e: React.ChangeEvent<HTMLInputElement> | CheckboxChangeEvent) => {
        const { name, checked, value } = e.target;
        setDetailCtg((prev) => ({
            ...prev,
            [name as string]: value != null ? value : checked ? 1 : 0,
        }));
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setDetailDetailCtg((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, []);

    const handleChangeSize = useCallback((size?: string[] | null) => {
        setDetailDetailCtg((prev) => ({
            ...prev,
            size: size ? [...size] : [],
        }));
    }, []);

    const handleSaveDetailCtg = useCallback(async () => {
        const data: Ctg = detailCtg;
        data.img = isChangeAvatar
            ? image?.name
            : detailCtg.img;
        if (!selectedId) {
            delete data.id;
        }
        delete data.products;
        try {
            const response = await ApiService.postCategory(data);
            if (response.status === "success") {
                toast.current?.show({
                    severity: "success",
                    summary: "Thành công",
                    detail:
                        (!!selectedId ? "Lưu" : "Thêm mới") +
                        " thành công !",
                });
                if (isChangeAvatar) {
                    await uploadAvatar();
                }
                setVisibleRight(false);
                fetchData();
            }
        } catch (error) {
            console.log(error);
        }
    }, [detailCtg, isChangeAvatar, image, selectedId, uploadAvatar, fetchData]);

    const submitDetailDetail = useCallback(async () => {
        const data: any = { ...detailDetailCtg };
        data.size = data.size ? data.size.join(',') : data.size;
        if (!selectedDetailId || selectedDetailId == "" || data.id == "" || !data.id) {
            delete data.id;
        }
        delete data.product_count;
        try {
            const response = await ApiService.postCategoryDetail(data);
            if (response.status === "success") {
                toast.current?.show({
                    severity: "success",
                    summary: "Thành công",
                    detail:
                        (!!selectedDetailId ? "Lưu" : "Thêm mới") +
                        " thành công !",
                });
                fetchData();
                setVisibleLeft(false);
            }
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Thông báo",
                detail: "Không thành công !",
                life: 2000,
            });
        }
    }, [detailDetailCtg, selectedDetailId, fetchData]);

    const handleAddCtg = useCallback(() => {
        setSelectedId(undefined);
        setVisibleRight(true);
        setDetailCtg(new Ctg());
        setImageUrl("");
    }, []);

    const addCtgDetail = useCallback(() => {
        setVisibleLeft(true);
        const newData: CategoryDetail = new CategoryDetail();
        newData.category_id = selectedId || "";
        setDetailDetailCtg(newData);
    }, [selectedId]);

    const rowExpansionTemplate = useCallback((data: CombinedCategory) => {
        return (
            <>
                <div>
                    <DataTable value={data.childs}>
                        <Column
                            className="w-4rem"
                            header="#"
                            body={(data, options) => options.rowIndex + 1}
                        ></Column>
                        <Column field="name" header="Loại chi tiết"></Column>
                        <Column
                            header="Số sản phẩm"
                            field="product_count"
                        ></Column>
                        <Column field="size" header="Phiên bản"></Column>
                        <Column body={optionsTemplate2}></Column>
                    </DataTable>
                </div>
                <OverlayPanel ref={op2}>
                    <div
                        className="sort_option"
                        onClick={handleEditDetailDetailCtg}
                    >
                        <span className="mr-2">
                            <i className="pi pi-pencil"></i>
                        </span>
                        Chỉnh sửa
                    </div>
                    <div
                        className="sort_option"
                        onClick={confirmDeleteCtgDetail}
                    >
                        <span className="mr-2">
                            <i className="pi pi-trash text-red-500"></i>
                        </span>
                        Xóa
                    </div>
                </OverlayPanel>
            </>
        );
    }, [optionsTemplate2, handleEditDetailDetailCtg, confirmDeleteCtgDetail]);

    const openImagePicker = useCallback(() => {
        setShowImagePicker(true);
    }, []);

    const handleImagePickerHide = useCallback(() => {
        setShowImagePicker(false);
    }, []);

    const handleImageSelect = useCallback((selectedImageUrl: string) => {
        setImageUrl(selectedImageUrl);
        setIsChangeAvatar(true);
        // Set a dummy file object to maintain compatibility with existing logic
        setImage(new File([], selectedImageUrl));
    }, []);

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="wrapper">
                <div className="header">
                    <BreadCrumb model={BREADCRUMB_ITEMS} home={HOME_BREADCRUMB} />
                    <div className="grid">
                        <div className="col-6 header-left flex">
                            <div className="empty"></div>
                            <div className="main-title flex align-items-center ml-2">
                                Loại
                            </div>
                        </div>
                        <div className="col-6 header-right flex align-items-center justify-content-end">
                            <div className="add-btn">
                                <Button
                                    onClick={handleAddCtg}
                                    label="Thêm mới"
                                    icon="pi pi-plus"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card">
                    <div>
                        <DataTable
                            value={combinedData}
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            dataKey="id"
                        >
                            <Column
                                expander={allowExpansion}
                                style={{ width: "5rem" }}
                            />
                            <Column
                                field="img"
                                header="Hình ảnh"
                                body={imageBodyTemplate}
                            ></Column>
                            <Column field="code" header="Mã"></Column>
                            <Column field="name" header="Tên"></Column>
                            <Column field="order" header="STT"></Column>
                            <Column
                                field="status"
                                header="Trạng thái"
                                body={statusTemplate}
                            ></Column>
                            <Column
                                field="is_show_home"
                                header="HT Trang chủ"
                                body={showHomeTemplate}
                            ></Column>
                            <Column body={optionsTemplate}></Column>
                        </DataTable>
                    </div>
                </div>
            </div>
            <OverlayPanel ref={op}>
                <div className="sort_option" onClick={addCtgDetail}>
                    <span className="mr-2">
                        <i
                            className="pi pi-plus"
                            style={{ color: "#007bff" }}
                        ></i>
                    </span>
                    Thêm
                </div>
                <div className="sort_option" onClick={viewCtgDetail}>
                    <span className="mr-2">
                        <i className="pi pi-pencil"></i>
                    </span>
                    Chỉnh sửa
                </div>
                <div className="sort_option" onClick={confirmDelete}>
                    <span className="mr-2">
                        <i className="pi pi-trash text-red-500"></i>
                    </span>
                    Xóa
                </div>
            </OverlayPanel>
            <Sidebar
                visible={visibleRight}
                className="w-6"
                position="right"
                onHide={() => setVisibleRight(false)}
            >
                <h2>Loại</h2>
                <div className="grid catgory">
                    <div className="col-12 avatar">
                        <img
                            className="w-full"
                            onClick={openImagePicker}
                            src={imageUrl || EMPTY_IMAGE_URL}
                            alt={imageUrl || "error"}
                        />
                    </div>
                    <div className="col-6">
                        <div>Mã</div>
                        <InputText
                            className="w-full"
                            value={detailCtg.code}
                            name="code"
                            onChange={handleCtgChange}
                            disabled={!!selectedId}
                        />
                    </div>
                    <div className="col-6">
                        <div>Tên</div>
                        <InputText
                            className="w-full"
                            value={detailCtg.name}
                            name="name"
                            onChange={handleCtgChange}
                        />
                    </div>
                    <div className="col-6">
                        <div>STT</div>
                        <InputText
                            className="w-full"
                            value={detailCtg.order.toString()}
                            name="order"
                            onChange={handleCtgChange}
                        />
                    </div>
                    <div className="col-3">
                        <div>Trạng thái</div>
                        <Checkbox
                            name="status"
                            onChange={handleCtgChange}
                            checked={detailCtg.status === 1 ? true : false}
                        ></Checkbox>
                    </div>
                    <div className="col-3">
                        <div>Hiển thị Trang chủ</div>
                        <Checkbox
                            name="is_show_home"
                            onChange={handleCtgChange}
                            checked={
                                detailCtg.is_show_home === 1 ? true : false
                            }
                        ></Checkbox>
                    </div>
                </div>
                <div className="flex mt-5 mr-2 justify-content-end">
                    <div
                        className="cancel-btn mr-2"
                        onClick={() => setVisibleRight(false)}
                    >
                        <Button label="Hủy" style={{ height: "40px" }} />
                    </div>
                    <div className="save-btn" onClick={handleSaveDetailCtg}>
                        <Button label="Lưu" style={{ height: "40px" }} />
                    </div>
                </div>
                <ImagePickerDialog
                    visible={showImagePicker}
                    onHide={handleImagePickerHide}
                    onImageSelect={handleImageSelect}
                    title="Chọn ảnh từ thư viện"
                />
            </Sidebar>

            <Dialog
                header="Loại chi tiết"
                draggable={false}
                visible={visibleLeft}
                style={{ width: "600px" }}
                onHide={() => {
                    if (!visibleLeft) return;
                    setVisibleLeft(false);
                }}
            >
                <div className="grid">
                    <div className="col-12">
                        <div>Loại</div>
                        <Dropdown
                            className="w-full"
                            options={listCtg}
                            value={detailDetailCtg.category_id}
                            disabled={true}
                        />
                    </div>
                    <div className="col-12">
                        <div>Tên</div>
                        <InputText
                            className="w-full"
                            name="name"
                            value={detailDetailCtg.name}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-12">
                        <div>Phân loại</div>
                        <Chips value={detailDetailCtg.size} onChange={(e) => handleChangeSize(e.value)} />
                    </div>
                </div>
                <div className="flex mt-5 mr-2 justify-content-end">
                    <div
                        className="cancel-btn mr-2"
                        onClick={() => setVisibleLeft(false)}
                    >
                        <Button label="Hủy" style={{ height: "40px" }} />
                    </div>
                    <div className="save-btn" onClick={submitDetailDetail}>
                        <Button label="Lưu" style={{ height: "40px" }} />
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default Category;
